<script setup>
import { computed } from 'vue'
import Checkbox from './Checkbox'
import DropDown from './DropDown.vue'
import DynamicInput from './DynamicInput'
import ErrorBlock from './ErrorBlock'
import Input from './Input'
import LocaleDropDown from './LocaleDropDown'
import NewFormActions from './NewFormActions'

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
  client: {
    type: Object,
    required: true,
  },
  storeGroups: {
    type: Array,
    required: true,
  },
  action: {
    type: Function,
    required: true,
  },
  buttonName: {
    type: String,
  },
})

const _updateField = (context, field) => (value) => context.update({ path: `selectedClient.${field}`, value })

const fields = [
  { type: 'text', key: 'name', label: 'Name', placeholder: 'E.g. Standard' },
  { type: 'text', key: 'share', label: 'Share (%)', placeholder: 'E.g 10', isPercentage: true },
  { type: 'text', key: 'instagram_share', label: 'Instagram share (%)', placeholder: 'E.g. 10', isPercentage: true },
  { type: 'button', key: 'validates_users', label: 'Validates Users', multiple: true },
  { type: 'button', key: 'default', label: 'Set as Default' },
]

const toggleShowOrgForm = computed({
  // getter
  get() {
    return props.context.showPaymentOrgForm
  },
  // setter
  set(value) {
    props.context.update({ path: 'showPaymentOrgForm', value })
  },
})
</script>

<template>
  <form>
    <Input
      id="edit-client-name"
      label="Name"
      placeholder="E.g. Metapic or Nouw"
      :value="client.name"
      :update="_updateField(context, 'name')"
    />
    <p>ID:</p>
    <div class="wrap-text">{{ client.id }}</div>

    <LocaleDropDown
      id="edit-client-locale"
      :value="client.locale"
      :update="_updateField(context, 'locale')"
      :default="{ value: 'Choose locale', unselectable: true }"
    />

    <p>Payments:</p>

    <Checkbox label="Own payment system" :checked="client.ownPaymentSystem" :update="_updateField(context, 'ownPaymentSystem')" />

    <Input
      id="edit-client-revenue-share"
      label="Revenue Share (%)"
      placeholder="E.g. 0.10"
      :value="client.revenue.share"
      :is-percentage="true"
      :update="_updateField(context, 'revenue.share')"
    />

    <DropDown
      id="edit-client-store-group"
      label="Store Group"
      :value="client.storeGroup"
      :update="_updateField(context, 'storeGroup')"
      :items="storeGroups"
      :default="{ value: 'Choose store group', unselectable: true }"
    />

    <DropDown
      id="edit-client-payment-organization"
      label="Payment Organization"
      :value="client.paymentOrgId"
      :update="_updateField(context, 'paymentOrgId')"
      :items="context.paymentOrganizations"
      :default="{ value: 'Choose payment organization', unselectable: true }"
    />

    <div class="align-right">
      <button
        type="button"
        class="link-basic"
        style="font-size: 0.74em"
        @click="context.update({ path: 'showPaymentOrgForm', value: true })"
      >
        <plus-icon /><span>Create new payment organization</span>
      </button>
    </div>

    <el-dialog v-model="toggleShowOrgForm" title="Create new payment organization" width="35%">
      <Input
        id="client-payment-org-name"
        label="Payment Organization Name"
        placeholder="E.g. Metapic or Nouw"
        :value="client.paymentOrgName"
        :update="_updateField(context, 'paymentOrgName')"
      />
      <Input
        id="client-payment-org-email"
        label="Payment Organization Email"
        placeholder="E.g. Erik@Metapic.com"
        :value="client.paymentOrgEmail"
        :update="_updateField(context, 'paymentOrgEmail')"
      />

      <template #footer>
        <div v-if="context.get('addingPaymentOrgSuccess')" class="success">{{ context.get('addingPaymentOrgSuccess') }}</div>
        <ErrorBlock v-if="context.get('addingPaymentOrgError')" :error="context.get('addingPaymentOrgError')" />
        <div>
          <button class="info" style="margin-right: 6px" @click="() => context.update({ path: 'showPaymentOrgForm', value: false })">
            Cancel
          </button>
          <button v-if="context.get('addingNewPaymentOrg')" class="success" disabled>
            <div class="spinner"></div>
          </button>
          <button v-else class="success" @click="() => context.addNewPaymentOrganization()">Create</button>
        </div>
      </template>
    </el-dialog>

    <Checkbox :checked="client.defaultVerifiedUsers" :update="_updateField(context, 'defaultVerifiedUsers')" label="Default verify users" />

    <DynamicInput
      id="edit-client-revenue-tiers"
      label="Revenue Tiers"
      :fields="fields"
      :items="client.revenue.tiers"
      :addItem="() => context.addTier()"
      :updateItem="(id, key, value) => context.update({ path: `selectedClient.revenue.tiers[${id}].${key}`, value })"
      :removeItem="(id) => context.removeTier(id)"
      add-label="Add Tier"
    />

    <p>User Mail Templates:</p>

    <div class="client-input-grid">
      <div v-for="userAction in context.userActions" :key="userAction.name">
        <DropDown
          :label="userAction.name"
          :value="client.userMailConfig?.[userAction.key] || ''"
          :update="(value) => context.setUserMailTemplate({ key: userAction.key, value })"
          :items="context.userMailTemplates.map((template) => ({ value: template.slug, label: template.name }))"
          default="Choose mail template"
        />
      </div>
    </div>

    <NewFormActions :action="action" :context="context" :reset="context.resetClient" :button-name="buttonName" />
  </form>
</template>
